import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { formatMoney, formatDate, daysUntil } from "@/lib/format";
import TransactionDialog from "@/components/TransactionDialog";
import { monthLabel } from "@/components/MonthFilter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  CalendarClock,
  Plus,
  Sparkles,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from "recharts";

const Card = ({ children, className = "", ...props }) => (
  <div
    className={`bg-[#131924] border border-white/5 rounded-xl ${className}`}
    {...props}
  >
    {children}
  </div>
);

const MetricCard = ({ label, value, hint, icon: Icon, accent, testid }) => (
  <Card className="p-5" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{label}</div>
        <div className={`font-mono-num text-2xl md:text-3xl font-bold tracking-tight mt-2 ${accent || "text-slate-100"}`}>
          {value}
        </div>
        {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
      </div>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent ? accent.replace("text-", "bg-").replace("-400", "-500/10") : "bg-indigo-500/10"}`}>
        <Icon className={`w-4 h-4 ${accent || "text-indigo-400"}`} />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const { base, month, monthQuery } = useCurrency();
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, tx, up, c] = await Promise.all([
        api.get(`/summary?base=${base}&${monthQuery}`),
        api.get(`/transactions?status=completed&${monthQuery}`),
        api.get(`/transactions?status=pending&${monthQuery}`),
        api.get("/categories"),
      ]);
      setSummary(s.data);
      setRecent(tx.data.slice(0, 6));
      setUpcoming(
        up.data
          .slice()
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(0, 5)
      );
      setCats(c.data);
    } catch (e) { /* ignore */ }
  }, [base, monthQuery]);

  useEffect(() => { load(); }, [load]);

  const catName = (id) => cats.find((c) => c.id === id)?.name || "Kategorisiz";
  const catColor = (id) => cats.find((c) => c.id === id)?.color || "#64748B";

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-400">Anlık Nakit Akışı</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-1">
            Finansal Özet
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Baz para birimi: <span className="font-mono-num text-slate-200">{base}</span> · Dönem:{" "}
            <span className="text-slate-200" data-testid="dashboard-period-label">{monthLabel(month)}</span>
          </p>
        </div>
        <Button
          onClick={() => setOpen(true)}
          data-testid="dashboard-add-transaction"
          className="bg-indigo-500 hover:bg-indigo-600 text-white h-10"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Yeni İşlem
        </Button>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Mevcut Nakit (Net)"
          value={formatMoney(summary?.net_cash || 0, base)}
          hint="Gerçekleşen gelir - gider"
          icon={Wallet}
          accent="text-indigo-400"
          testid="metric-net-cash"
        />
        <MetricCard
          label="Toplam Gelir"
          value={formatMoney(summary?.income_completed || 0, base)}
          hint="Gerçekleşen"
          icon={ArrowUpRight}
          accent="text-emerald-400"
          testid="metric-income"
        />
        <MetricCard
          label="Toplam Gider"
          value={formatMoney(summary?.expense_completed || 0, base)}
          hint="Gerçekleşen"
          icon={ArrowDownRight}
          accent="text-rose-400"
          testid="metric-expense"
        />
        <MetricCard
          label="Projeksiyon"
          value={formatMoney(summary?.projected_cash || 0, base)}
          hint={`+${formatMoney(summary?.upcoming_receivables || 0, base)} / -${formatMoney(summary?.upcoming_payables || 0, base)}`}
          icon={Sparkles}
          accent="text-amber-400"
          testid="metric-projected"
        />
      </div>

      {/* Charts & upcoming */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">6 Aylık Trend</div>
              <h2 className="font-display text-lg font-bold tracking-tight mt-1">Nakit Akışı Grafiği</h2>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={summary?.monthly || []} margin={{ top: 10, right: 10, bottom: 0, left: 10 }}>
                <defs>
                  <linearGradient id="g-income" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="g-expense" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F43F5E" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#F43F5E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="label" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} width={50} />
                <Tooltip
                  contentStyle={{ background: "#0F1522", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10 }}
                  labelStyle={{ color: "#F8FAFC" }}
                  formatter={(v) => formatMoney(v, base)}
                />
                <Area type="monotone" dataKey="income" stroke="#10B981" strokeWidth={2} fill="url(#g-income)" name="Gelir" />
                <Area type="monotone" dataKey="expense" stroke="#F43F5E" strokeWidth={2} fill="url(#g-expense)" name="Gider" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Yaklaşan</div>
              <h2 className="font-display text-lg font-bold tracking-tight mt-1">Tahsilat & Ödeme</h2>
            </div>
            <CalendarClock className="w-4 h-4 text-amber-400" />
          </div>

          {upcoming.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">
              Yaklaşan kalem bulunmuyor.
            </div>
          ) : (
            <div className="space-y-2.5">
              {upcoming.map((u) => {
                const d = daysUntil(u.date);
                const overdue = d < 0;
                const dueToday = d === 0;
                return (
                  <div
                    key={u.id}
                    data-testid={`upcoming-item-${u.id}`}
                    className="flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.02] border border-white/5"
                  >
                    <div
                      className={`w-1 self-stretch rounded-full ${
                        u.type === "income" ? "bg-emerald-500" : "bg-rose-500"
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {u.counterparty || u.description || "İsimsiz"}
                      </div>
                      <div className="text-[11px] text-slate-500">{formatDate(u.date)}</div>
                    </div>
                    <div className="text-right">
                      <div className={`font-mono-num text-sm font-semibold ${u.type === "income" ? "text-emerald-400" : "text-rose-400"}`}>
                        {u.type === "income" ? "+" : "-"}{formatMoney(u.amount, u.currency)}
                      </div>
                      <Badge
                        variant="outline"
                        className={`mt-1 text-[10px] border-0 ${
                          overdue
                            ? "bg-rose-500/15 text-rose-300"
                            : dueToday
                            ? "bg-amber-500/15 text-amber-300"
                            : "bg-indigo-500/15 text-indigo-300"
                        }`}
                      >
                        {overdue ? `${Math.abs(d)} gün gecikti` : dueToday ? "Bugün" : `${d} gün kaldı`}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Currency breakdown & recent */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Para Birimi Dağılımı</div>
          <h2 className="font-display text-lg font-bold tracking-tight mt-1 mb-4">Mevcut Bakiye</h2>
          <div className="space-y-3">
            {Object.entries(summary?.by_currency || {}).map(([cur, v]) => (
              <div key={cur} className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] border border-white/5">
                <div>
                  <div className="font-display text-sm font-bold">{cur}</div>
                  <div className="text-[11px] text-slate-500 font-mono-num">
                    +{formatMoney(v.income, cur)} / -{formatMoney(v.expense, cur)}
                  </div>
                </div>
                <div className={`font-mono-num text-base font-bold ${v.net >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                  {formatMoney(v.net, cur)}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Aylık Karşılaştırma</div>
              <h2 className="font-display text-lg font-bold tracking-tight mt-1">Gelir vs Gider</h2>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={summary?.monthly || []} margin={{ top: 10, right: 10, bottom: 0, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="label" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} width={50} />
                <Tooltip
                  contentStyle={{ background: "#0F1522", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10 }}
                  formatter={(v) => formatMoney(v, base)}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="income" fill="#10B981" name="Gelir" radius={[6, 6, 0, 0]} />
                <Bar dataKey="expense" fill="#F43F5E" name="Gider" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Recent transactions */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Son Hareketler</div>
            <h2 className="font-display text-lg font-bold tracking-tight mt-1">Gerçekleşen İşlemler</h2>
          </div>
        </div>
        {recent.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">Henüz işlem yok. İlk işleminizi ekleyin.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {recent.map((t) => (
              <div key={t.id} className="py-3 flex items-center gap-4" data-testid={`recent-tx-${t.id}`}>
                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                    t.type === "income" ? "bg-emerald-500/10" : "bg-rose-500/10"
                  }`}
                >
                  {t.type === "income" ? (
                    <ArrowUpRight className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4 text-rose-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{t.description || t.counterparty || "İşlem"}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full"
                      style={{ background: catColor(t.category_id) + "22", color: catColor(t.category_id) }}
                    >
                      {catName(t.category_id)}
                    </span>
                    <span className="text-[11px] text-slate-500">{formatDate(t.date)}</span>
                  </div>
                </div>
                <div className={`font-mono-num font-semibold ${t.type === "income" ? "text-emerald-400" : "text-rose-400"}`}>
                  {t.type === "income" ? "+" : "-"}{formatMoney(t.amount, t.currency)}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <TransactionDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}
